// Crear un color que no está en la lista.
//
// POR QUÉ PIDE TRES COSAS Y NO UNA
//
// Un nombre solo no alcanza. La lista de colores no es un catálogo
// decorativo: alimenta dos mecanismos que necesitan saber más.
//
//   - La regla de estilo pregunta si el color es NEUTRO o FUERTE. Los
//     neutros van con todo; de los fuertes va uno solo por conjunto.
//     Un color sin esa marca quedaría en un limbo, y cualquiera de las
//     dos suposiciones rompe algo: asumir neutro haría que un fucsia
//     combine con cualquier cosa, y asumir fuerte haría que un beige
//     nuevo choque con medio armario.
//
//   - El filtro del armario pregunta a qué FAMILIA pertenece, para que
//     "mostrame algo azul" traiga los cinco azules juntos.
//
// Son dos clics más una vez, a cambio de que el color nuevo funcione
// igual que los 20 que ya venían.

import { useState } from "react";
import * as api from "./api";
import { invalidarOpciones } from "./opciones";
import { t, tCat, tError, useIdioma } from "./i18n";

export default function NuevoColor({ familias, muestras, onCreado, onCerrar }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  const [nombre, setNombre] = useState("");
  const [familia, setFamilia] = useState(familias?.[0] || "Negros");
  const [esNeutro, setEsNeutro] = useState(false);
  // Sin muestra elegida el color se dibuja con trama gris, que es
  // honesto pero inútil repetido treinta veces en un calendario. No se
  // preselecciona ninguna: un color elegido por descuido es peor que
  // ninguno, porque nadie vuelve a revisar lo que ya "eligió".
  const [hex, setHex] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  async function crear() {
    if (!nombre.trim()) return;

    setGuardando(true);
    setError("");
    try {
      const color = await api.crearColor({
        nombre: nombre.trim(),
        familia,
        es_neutro: esNeutro,
        hex,
      });
      // Mismo motivo que en NuevaOcasion: sin esto el color nuevo no
      // aparece en el filtro del armario hasta recargar.
      invalidarOpciones();
      setNombre("");
      setEsNeutro(false);
      setHex(null);
      onCreado?.(color);
    } catch (e2) {
      setError(tError(e2));
    } finally {
      setGuardando(false);
    }
  }

  /* ⚠️  ESTO NO ES UN <form>, Y NO PUEDE SERLO (2026-08-21).
   *
   * EL BUG QUE COSTÓ SEIS VUELTAS
   *
   * Este componente se renderiza DENTRO del <form> de EditarPrenda. HTML
   * PROHÍBE los formularios anidados: el parser del navegador descarta el
   * <form> interno y se queda solo con su contenido.
   *
   * El resultado no es un error — es algo peor:
   *
   *   - El `onSubmit` de acá NUNCA se dispara.
   *   - El <button type="submit"> pasa a ser el submit del formulario DE
   *     AFUERA.
   *   - Tocar "Crear" GUARDA LA PRENDA y cierra el editor.
   *
   * Desde afuera se ve idéntico a que funcionó: el panel se cierra, no
   * hay cartel rojo, y no se creó nada. El dueño reportó seis veces que
   * su ocasión "no aparecía" — nunca había llegado a existir.
   *
   * Este archivo fue el ORIGINAL del error: NuevaOcasion se copió de acá
   * meses después, con el <form> incluido. O sea que los colores propios
   * nunca funcionaron desde el primer día, y nadie lo notó — porque
   * nadie llegó a necesitar uno hasta que las ocasiones lo destaparon.
   *
   * LA REGLA: un componente que puede vivir adentro de otro no declara un
   * <form>. Usa un <div> y un botón normal con onClick. El costo es tener
   * que manejar Enter a mano; el beneficio es que funciona.
   */
  return (
    <div className="nuevo-color">
      {error && <div className="mensaje-error texto-mini" role="alert">{error}</div>}

      <label className="campo-mini">
        <span>{t("colores.nombre", "Nombre del color")}</span>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t("colores.ejemplos", "Fucsia, Crudo, Mostaza...")}
          maxLength={30}
          autoFocus
          // Enter para crear. Lo daba gratis el <form>; sin él hay que
          // ponerlo a mano, y vale la pena: escribir un nombre y apretar
          // Enter es lo que hace cualquiera.
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              crear();
            }
          }}
        />
      </label>

      <label className="campo-mini">
        <span>{t("colores.familia", "¿En qué familia lo agrupo? (para el filtro)")}</span>
        <select value={familia} onChange={(e) => setFamilia(e.target.value)}>
          {(familias || []).map((f) => (
            <option key={f} value={f}>
              {tCat("familiaColor", f)}
            </option>
          ))}
        </select>
      </label>

      {/* Neutro o fuerte, explicado por lo que HACE y no por la teoría
          del color. "Acromático" no le dice nada a nadie; "combina con
          todo" sí. */}
      <div className="campo-mini">
        <span>{t("colores.comoSeLleva", "¿Cómo se lleva con otros colores?")}</span>
        <div className="eleccion">
          <button
            type="button"
            className={`eleccion-op ${!esNeutro ? "eleccion-activa" : ""}`}
            onClick={() => setEsNeutro(false)}
          >
            <strong>{t("colores.fuerte", "Fuerte")}</strong>
            <span className="tenue texto-mini">
              {t("colores.fuerteExplica", "Llama la atención. Va uno solo por conjunto.")}
            </span>
          </button>
          <button
            type="button"
            className={`eleccion-op ${esNeutro ? "eleccion-activa" : ""}`}
            onClick={() => setEsNeutro(true)}
          >
            <strong>{t("colores.neutro", "Neutro")}</strong>
            <span className="tenue texto-mini">
              {t("colores.neutroExplica", "Combina con todo, como el negro o el beige.")}
            </span>
          </button>
        </div>
      </div>

      {/* La muestra.
          Una paleta acotada y no un `<input type="color">`. Un selector
          libre deja elegir 16 millones de tonos, y de esos el usuario
          elige uno que después no puede distinguir del que ya tenía: el
          calendario termina con cuatro grises casi iguales que no dicen
          nada. Veinticuatro separadas entre sí es lo que se mira de un
          vistazo y se distingue en un cuadrito de pocos píxeles, que es
          el tamaño real de uso. */}
      <div className="campo-mini">
        <span>{t("colores.comoLoDibujo", "¿Cómo lo dibujo cuando no hay foto?")}</span>
        <div className="muestras">
          {(muestras || []).map((m) => (
            <button
              key={m}
              type="button"
              className={`muestra ${hex === m ? "muestra-activa" : ""}`}
              style={{ background: m }}
              onClick={() => setHex(hex === m ? null : m)}
              aria-label={m}
              title={m}
            />
          ))}
        </div>
        <span className="tenue texto-mini">
          {hex
            ? t("colores.asiSeVe", "Así se va a ver en el calendario y en cada combinación.")
            : t("colores.sinMuestra", "Opcional. Sin muestra se dibuja con una trama gris.")}
        </span>
      </div>

      <div className="editor-acciones">
        <button
          className="boton-secundario"
          type="button"
          onClick={crear}
          disabled={guardando}
        >
          {guardando
            ? t("colores.creando", "Creando...")
            : t("colores.crear", "Crear color")}
        </button>
        <button className="boton-texto" type="button" onClick={onCerrar}>
          {t("colores.cancelar", "Cancelar")}
        </button>
      </div>
    </div>
  );
}
